/**
 * La rete di chi ha fatto accesso: nodi, livelli e cifre.
 *
 * Tre servizi rispondono di tre cose diverse - jurisdiction di chi sta sotto
 * chi, admin/types dei livelli, credit dei soldi - e una schermata le vuole
 * insieme. Il montaggio sta qui, non dentro il componente: cosi' la pagina si
 * occupa di disegnare e questo file di parlare coi servizi.
 *
 * Le chiamate sono TRE per l'intera pagina, non tre per riga.
 */

import { conCifre } from '../utils/arricchisci';
import { getHierarchyRules } from './usersFrontendApi';
import i18next from 'i18next';

function base() {
  const env = (typeof window !== 'undefined' && window.env) || {};
  return env.REACT_APP_API_URL || '';
}

function intestazioni() {
  const env = (typeof window !== 'undefined' && window.env) || {};
  const token = typeof window !== 'undefined'
      ? window.localStorage.getItem('access_token') : null;

  const h = {
    'Content-Type': 'application/json',
    'X-Client-Surface': 'FRONTEND',
    TENANT_ID: env.REACT_APP_API_TENANT || 1,
  };
  if (token) h.Authorization = `Bearer ${token}`;
  // USER_ID NON si manda da qui. Lo mette il gateway leggendolo dal gettone
  // (AuthorizationPreFilter), e mutate().header() SOSTITUISCE quello che
  // arriva dal browser. Scriverlo qui non servirebbe a niente e darebbe
  // l'idea sbagliata che sia il browser a decidere fin dove si vede: chi
  // legge il codice penserebbe che cambiando user_id nella memoria del
  // browser si vede la rete di un altro.
  return h;
}

/**
 * I nodi che posso vedere: il mio sottoalbero, io compreso.
 *
 * Il taglio lo fa il servizio a partire dall'identita' nel gettone, non il
 * browser: un filtro fatto qui sarebbe una cortesia verso l'occhio, non una
 * regola - basterebbe aprire gli strumenti per sviluppatori per vedere la
 * rete di qualcun altro.
 */
export async function nodiDellaMiaRete() {
  const res = await fetch(`${base()}/private/jurisdiction/all`, {
    headers: intestazioni(),
  });
  if (!res.ok) {
    throw new Error(i18next.t('Rete non disponibile ({{status}})', {status: res.status}));
  }
  const nodi = await res.json();
  return Array.isArray(nodi) ? nodi : [];
}

/**
 * Le persone dietro i nodi.
 *
 * jurisdiction sa dove sta ognuno nella rete, ma di suo ha solo lo userId, che
 * e' un UUID. Lo USERNAME - quello che una persona riconosce e pronuncia - sta
 * nell'elenco degli utenti, insieme a nome e cognome. Due servizi, due cose
 * diverse, e una schermata che le vuole tutte e due.
 *
 * Senza questa chiamata la tabella mostrava la rete giusta con dentro degli
 * identificativi illeggibili: la struttura era corretta e nessuno la sapeva
 * leggere.
 */
async function profili() {
  try {
    const res = await fetch(`${base()}/admin/users?username=`, {
      headers: intestazioni(),
    });
    if (!res.ok) return {};
    const elenco = await res.json();
    const perId = {};
    for (const u of (Array.isArray(elenco) ? elenco : elenco.users || [])) {
      perId[u.userId] = u;
    }
    return perId;
  } catch (e) {
    // Senza i nomi la rete si mostra lo stesso: meglio una tabella con gli
    // identificativi che nessuna tabella.
    console.warn('Nomi non disponibili:', e.message);
    return {};
  }
}

/**
 * Tutto quello che serve alla pagina, in una volta.
 *
 * Se le cifre non arrivano, i nodi si mostrano lo stesso e le colonne restano
 * vuote: conCifre restituisce l'elenco com'era. Un trattino dice "non lo so";
 * uno zero direbbe "e' zero", che e' un'altra cosa e sui soldi non si inventa.
 */
export async function laMiaRete() {
  const [nodi, livelli, gente] = await Promise.all([
    nodiDellaMiaRete(),
    getHierarchyRules(),
    profili(),
  ]);

  // Il servizio parla di userId, la schermata di persone: la traduzione sta
  // qui, in un punto solo. `userId` resta - serve a chiedere le cifre, i
  // movimenti e a fare i giroconti - ma non e' piu' quello che si legge.
  const conNomi = nodi.map((n) => {
    const p = gente[n.userId];
    if (!p) return n;
    const persona = [p.name, p.surname].filter(Boolean).join(' ');
    return {
      ...n,
      username: p.username,
      persona: persona || undefined,
    };
  });

  return { nodi: await conCifre(conNomi), livelli };
}

/**
 * La rete con i nomi ma SENZA le cifre dei conti: i nodi che si vedono, con
 * lo username. Per le pagine che disegnano la rete senza i saldi (il report):
 * le cifre sono un'altra chiamata al credit-service per ogni pagina.
 */
export async function reteSenzaCifre() {
  const [nodi, gente] = await Promise.all([nodiDellaMiaRete(), profili()]);
  return nodi.map((n) => {
    const p = gente[n.userId];
    return p ? { ...n, username: p.username } : n;
  });
}

/** Le cinque cifre aggiornate di una posizione sola, dopo un'operazione. */
export async function cifreDi(userId) {
  const res = await fetch(`${base()}/private/credit/${userId}/figures`, {
    headers: intestazioni(),
  });
  if (!res.ok) return null;
  const f = await res.json();
  return {
    fidoRete: f.networkCredit,
    disponibilita: f.available,
    saldoUtente: f.userBalance,
    saldoTotRete: f.networkBalance,
    fidoDisp: f.creditAvailable,
    currency: f.currency,
  };
}

/**
 * Cerca una posizione nella rete, CHIEDENDOLO AL SERVIZIO.
 *
 * Non scarica niente in anticipo: manda quello che e' stato scritto e riceve i
 * pochi che corrispondono. E' la differenza fra una cosa che funziona con
 * quindici posizioni e una che funziona anche con un milione - e il taglio
 * sulla propria rete lo fa il servizio leggendo l'identita' dal gettone, non
 * il browser.
 */
export async function cercaNellaRete(testo) {
  const q = (testo || '').trim();
  if (q.length < 2) return [];          // una lettera sola riporterebbe mezza rete

  const res = await fetch(
      `${base()}/admin/users?username=${encodeURIComponent(q)}`,
      { headers: intestazioni() });
  if (!res.ok) {
    throw new Error(res.status === 403
        ? i18next.t('Non hai il permesso di consultare l\'elenco degli utenti')
        : i18next.t('Ricerca non disponibile ({{status}})', {status: res.status}));
  }

  const dati = await res.json();
  const elenco = Array.isArray(dati) ? dati : (dati.users || []);
  return elenco.map((u) => ({
    userId: u.userId,
    username: u.username,
    persona: [u.name, u.surname].filter(Boolean).join(' ') || undefined,
  }));
}

/** Il nodo nella rete: serve il livello, per dire di chi si tratta. */
export async function nodoDi(userId) {
  try {
    const res = await fetch(`${base()}/private/jurisdiction/${userId}`, {
      headers: intestazioni(),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    return null;
  }
}
